import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type HTMLAttributes,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { clsx } from '../clsx';
import { Button } from './Button';
import styles from './Toast.module.css';

export interface ToastOptions {
  message: ReactNode;
  /** Undo-style action ("Rückgängig"); the toast closes after it ran. */
  action?: { label: string; onClick: () => void } | undefined;
  /** Milliseconds until the toast disappears (default 6000; the reservation undo uses 30000). */
  duration?: number | undefined;
  /** Shows "noch N s" and the countdown bar (default: on when there is an action). */
  countdown?: boolean | undefined;
  /** Called when the toast disappears without the action having run. */
  onExpire?: (() => void) | undefined;
}

export interface ToastHandle {
  id: number;
  dismiss: () => void;
}

interface ToastApi {
  toast: (options: ToastOptions) => ToastHandle;
  dismiss: (id: number) => void;
}

interface ToastEntry extends ToastOptions {
  id: number;
}

const ToastContext = createContext<ToastApi | null>(null);

/** Access to the toast queue; requires `ToastProvider` above. */
export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error('useToast() needs a <ToastProvider>');
  return api;
}

const DEFAULT_DURATION = 6000;

export interface ToastProps extends HTMLAttributes<HTMLDivElement> {
  message: ReactNode;
  actionLabel?: string | undefined;
  onAction?: (() => void) | undefined;
  /** Remaining and total time drive the "noch N s" text and the bar; omit both for a toast without countdown. */
  remainingMs?: number | undefined;
  durationMs?: number | undefined;
}

/** The toast itself (Reservierung 13): navy-900, bottom centred, countdown text, ghost action, countdown bar. */
export function Toast({
  message,
  actionLabel,
  onAction,
  remainingMs,
  durationMs,
  className,
  ...rest
}: ToastProps) {
  const countdown = remainingMs !== undefined && durationMs !== undefined && durationMs > 0;
  const seconds = countdown ? Math.ceil(remainingMs / 1000) : 0;
  return (
    <div role="status" className={clsx(styles.toast, className)} {...rest}>
      <span className={styles.message}>{message}</span>
      {countdown ? <span className={styles.countdown}>noch {seconds}&nbsp;s</span> : null}
      {actionLabel ? (
        <Button variant="ghostInverse" size="small" className={styles.action} onClick={onAction}>
          {actionLabel}
        </Button>
      ) : null}
      {countdown ? (
        <span aria-hidden="true" className={styles.bar}>
          <span className={styles.barFill} style={{ width: `${(remainingMs / durationMs) * 100}%` }} />
        </span>
      ) : null}
    </div>
  );
}

function ToastTimer({ entry, onDone }: { entry: ToastEntry; onDone: (ran: boolean) => void }) {
  const duration = entry.duration ?? DEFAULT_DURATION;
  const countdown = entry.countdown ?? Boolean(entry.action);
  const [remaining, setRemaining] = useState(duration);
  const paused = useRef(false);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;

  useEffect(() => {
    let last = performance.now();
    let left = duration;
    const timer = window.setInterval(() => {
      const now = performance.now();
      if (!paused.current) {
        left -= now - last;
        setRemaining(Math.max(0, left));
        if (left <= 0) {
          window.clearInterval(timer);
          doneRef.current(false);
        }
      }
      last = now;
    }, 100);
    return () => window.clearInterval(timer);
  }, [duration]);

  return (
    <Toast
      message={entry.message}
      actionLabel={entry.action?.label}
      onAction={() => {
        entry.action?.onClick();
        onDone(true);
      }}
      remainingMs={countdown ? remaining : undefined}
      durationMs={countdown ? duration : undefined}
      onMouseEnter={() => {
        paused.current = true;
      }}
      onMouseLeave={() => {
        paused.current = false;
      }}
      onFocus={() => {
        paused.current = true;
      }}
      onBlur={() => {
        paused.current = false;
      }}
    />
  );
}

/** Mounts the toast region (bottom centre, `--z-toast`) and provides `useToast()`. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [entries, setEntries] = useState<ToastEntry[]>([]);
  const counter = useRef(0);

  const dismiss = useCallback((id: number) => {
    setEntries((list) => list.filter((entry) => entry.id !== id));
  }, []);

  const toast = useCallback(
    (options: ToastOptions): ToastHandle => {
      counter.current += 1;
      const id = counter.current;
      setEntries((list) => [...list.slice(-2), { ...options, id }]);
      return { id, dismiss: () => dismiss(id) };
    },
    [dismiss],
  );

  const api = useMemo(() => ({ toast, dismiss }), [toast, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div className={styles.region} aria-live="polite" aria-relevant="additions">
          {entries.map((entry) => (
            <ToastTimer
              key={entry.id}
              entry={entry}
              onDone={(ran) => {
                if (!ran) entry.onExpire?.();
                dismiss(entry.id);
              }}
            />
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}
